import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiUrl, mockAuthHeaders } from '../lib/api';

export interface Skill {
  id: string;
  name: string;
  description: string | null;
  personCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSkillRequest {
  name: string;
  description?: string;
}

async function fetchSkills(): Promise<Skill[]> {
  const response = await fetch(apiUrl('/api/v1/skills'), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch skills');
  }

  const json = (await response.json()) as { data?: Skill[] };
  return json.data ?? [];
}

async function createSkill(data: CreateSkillRequest): Promise<{ id: string }> {
  const response = await fetch(apiUrl('/api/v1/skills'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(error.error?.message ?? 'Failed to create skill');
  }

  const json = (await response.json()) as { data?: { id: string } };
  return json.data ?? { id: '' };
}

async function deleteSkill(id: string): Promise<{ deleted: boolean }> {
  const response = await fetch(apiUrl(`/api/v1/skills/${id}`), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(error.error?.message ?? 'Failed to delete skill');
  }

  const json = (await response.json()) as { data?: { deleted: boolean } };
  return json.data ?? { deleted: false };
}

export function useSkills() {
  return useQuery({
    queryKey: ['skills'],
    queryFn: fetchSkills,
    staleTime: 60000, // Skills change less frequently
  });
}

export function useCreateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSkill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}

export function useDeleteSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSkill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}
